import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MapPin, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import RegionalVibeCheck from "../components/report/RegionalVibeCheck";
import EnergyGridCard from "../components/report/EnergyGridCard";
import { API } from "../App";

const SharedReportPage = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const response = await axios.get(`${API}/reports/shared/${shareToken}`);
        setReport(response.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This share link is invalid or has been revoked." : "Failed to load shared guide.");
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [shareToken]);

  const keyStatsEntries = useMemo(() => {
    if (!report?.content?.key_stats) return [];
    return Object.entries(report.content.key_stats).filter(([k]) => !k.includes("_usa"));
  }, [report]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !report?.content) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">Guide Not Available</h2>
          <p className="text-muted-foreground mb-6">{error || "No content available."}</p>
          <Button onClick={() => navigate("/")} data-testid="shared-back-home-btn">
            Back to ExpatMatch
          </Button>
        </div>
      </div>
    );
  }

  const { country_name, content } = report;

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="shared-report-page">
      <div className="max-w-4xl mx-auto">
        {/* Banner */}
        <div className="bg-primary/5 border border-primary/20 rounded-xl px-5 py-3 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-sm text-primary">
            <Globe className="h-4 w-4" />
            <span>
              <strong>Shared Relocation Guide</strong> — read-only preview
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/")}
            className="bg-primary hover:bg-primary/90 text-white"
            data-testid="shared-cta-btn"
          >
            Create your own guide →
          </Button>
        </div>

        {/* Title */}
        <div className="bg-card rounded-2xl border border-primary/10 p-8 mb-6">
          <div className="flex items-start gap-3 mb-2">
            <MapPin className="h-6 w-6 text-secondary mt-1" />
            <h1 className="text-3xl md:text-4xl font-bold text-primary" data-testid="shared-report-country">
              {country_name}
            </h1>
          </div>
          <p className="text-muted-foreground">Personalized Relocation Decision Guide</p>
        </div>

        {/* Key Stats */}
        {content.key_stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            {keyStatsEntries.map(([key, value]) => {
                const usaValue = content.key_stats[`${key}_usa`];
                return (
                  <div key={key} className="bg-card border border-primary/10 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-primary">{value}</div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {key.replace(/_/g, " ")}
                    </div>
                    {usaValue !== undefined && (
                      <p className="text-xs text-accent mt-1">vs USA: {usaValue}</p>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {/* Quick Facts */}
        {content.quick_facts?.length > 0 && (
          <div className="bg-card border border-primary/10 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-primary mb-3">Quick Facts</h3>
            <ul className="space-y-2">
              {content.quick_facts.map((fact, i) => (
                <li key={`fact-${i}-${fact.slice(0, 20)}`} className="text-sm text-muted-foreground flex gap-2">
                  <span className="text-secondary">•</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comparative Regional Analysis */}
        <RegionalVibeCheck regional={content.regional_analysis} />

        {/* Energy Grid Dependability */}
        <EnergyGridCard energy={content.energy_grid} />

        {/* Sections */}
        <div className="space-y-4">
          {(content.sections || []).map((section, i) => (
            <div key={section.title || `section-${i}`} className="bg-card border border-primary/10 rounded-xl p-6">
              <h3 className="font-bold text-primary text-lg mb-3">{section.title}</h3>
              <p className="text-muted-foreground whitespace-pre-line leading-relaxed">
                {section.content}
              </p>
              {section.subsections?.map((sub, j) => (
                <div key={sub.title || `sub-${j}`} className="mt-4 pl-4 border-l-2 border-primary/20">
                  <h4 className="font-semibold text-primary mb-1">{sub.title}</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {sub.content}
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-10 text-center py-8 px-6 bg-primary/5 rounded-2xl border border-primary/10">
          <h3 className="text-xl font-bold text-primary mb-2">
            Ready to find your own perfect destination?
          </h3>
          <p className="text-muted-foreground mb-4">
            Get a personalized relocation guide built around your lifestyle and budget.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="btn-animated bg-primary hover:bg-primary/90"
            data-testid="shared-footer-cta-btn"
          >
            Start your free assessment →
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SharedReportPage;
